import React, { useEffect, useState } from 'react'
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import AmbulanceIcon from '../../../assets/icons/Ambulance'
import OpenInNew from '@mui/icons-material/OpenInNew'
import ChecklistIcon from '@mui/icons-material/Checklist'
import API from '../../../services/API'
import moment from 'moment'
import { Circle } from '@mui/icons-material'
import { LOLER_STATE } from './VehicleComponents/Loler/lolerOptions'

// Server `lolerStatus` virtual → list cell. Null = LOLER doesn't apply.
// Also reused by the fleet list's Print PDF.
export const lolerCellText = (status) => {
  if (!status) return ''
  if (status.state === 'missing') return 'No record'
  if (status.state === 'failed') return 'Failed'
  return status.nextDue ? moment(status.nextDue).format('DD/MM/YYYY') : ''
}

// `onMakeReady(vehicleId)` adds a per-row Make Ready action; omit it (e.g.
// on tiers without Make Ready) to leave the column out.
const Columns = ({ onMakeReady, ...orgSettings }) => {
  const makeReadyColumn = onMakeReady
    ? [
        {
          field: 'makeReady',
          headerName: 'Make Ready',
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          filterable: false,
          disableExport: true,
          minWidth: 110,
          renderCell: ({ row }) => {
            const hasLoadList = !!row.makeReadyLoadList
            return (
              <Tooltip
                title={
                  hasLoadList ? 'Run Make Ready check' : 'No load list assigned'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label="Run Make Ready check"
                    disabled={!hasLoadList}
                    onClick={(e) => {
                      e.stopPropagation()
                      onMakeReady(row._id)
                    }}
                  >
                    <ChecklistIcon
                      fontSize="small"
                      color={hasLoadList ? 'primary' : 'disabled'}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            )
          },
        },
      ]
    : []

  return [
    {
      field: 'callSign',
      headerName: 'Call Sign',
      minWidth: 90,
      flex: 0.7,
    },
    {
      field: 'image',
      disableReorder: true,
      headerName: 'Image',
      align: 'center',
      headerAlign: 'center',
      disableExport: true,
      renderCell: ({ row }) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
        >
          <Avatar
            src={row.image}
            sx={{ width: 32, height: 32, borderRadius: 1 }}
          >
            <AmbulanceIcon />
          </Avatar>
        </Box>
      ),
    },
    {
      field: 'numPlate',
      headerName: 'Registration',
      flex: 0.4,
      headerAlign: 'center',
      getOptionValue: ({ row }) => row?.callSign,
      valueGetter: (_, row) => row?.callSign ?? null,
      minWidth: 140,
      renderCell: ({ row }) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
          alignSelf="center"
          height="100%"
        >
          <Box
            padding={0.5}
            borderRadius={1}
            sx={{ backgroundColor: '#FFD307' }}
            width="100%"
          >
            <Typography
              fontFamily="UKnumberplate"
              textAlign="center"
              // fontSize={20}
              // padding={1}
            >
              {row.regPlate}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 80,
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      getOptionValue: ({ row }) => row.status,
      renderCell: ({ row }) => (
        <Tooltip title={row.status}>
          <Circle
            label={row.status}
            color={
              row.status === 'Out of Service'
                ? 'anRed'
                : row.status === 'Operational'
                  ? 'anGreen'
                  : 'anBlue'
            }
          />
        </Tooltip>
      ),
    },
    {
      field: 'vehicleType',
      headerName: 'Type',
      // renderCell: () => (
      //   <Box
      //     display="flex"
      //     justifyContent="center"
      //     alignItems="center"
      //     width="100%"
      //     height="100%"
      //   >
      //     <AmbulanceIcon />
      //   </Box>
      // ),
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'baseLocation',
      headerName: 'Location',
      valueGetter: (_, row) => {
        return row?.baseLocation?.name ?? null
      },
      flex: 1,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'activeDefects',
      headerName: 'Defects',
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) =>
        row.activeDefects > 0 && (
          <Chip color="error" label={row.activeDefects} />
        ),
    },
    ...makeReadyColumn,

    {
      field: 'lolerStatus',
      headerName: 'LOLER',
      align: 'center',
      headerAlign: 'center',
      flex: 0.3,
      minWidth: 130,
      // Sort/filter/export on the next-due date; failed and missing sort first
      valueGetter: (_, row) => {
        const status = row?.lolerStatus
        if (!status) return null
        if (status.state === 'failed' || status.state === 'missing') return new Date(0)
        return status.nextDue ? new Date(status.nextDue) : null
      },
      valueFormatter: (_, row) => lolerCellText(row?.lolerStatus),
      renderCell: ({ row }) => {
        const status = row?.lolerStatus
        if (!status) return '-'
        const meta = LOLER_STATE[status.state]
        const flagged = status.state !== 'ok'
        return (
          <Tooltip title={meta?.label ?? ''}>
            <Box
              height="100%"
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {flagged ? (
                <Chip
                  size="small"
                  label={lolerCellText(status)}
                  color={meta?.tone}
                  sx={{ borderRadius: 1, fontWeight: 700 }}
                />
              ) : (
                lolerCellText(status)
              )}
            </Box>
          </Tooltip>
        )
      },
    },
    {
      field: 'lastDeepClean',
      headerName: 'Last Deep Clean',
      align: 'center',
      headerAlign: 'center',
      flex: 0.3,
      minWidth: 140,
      valueGetter: (_, row) => row?.lastDeepClean?.end ?? null,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : '',
      renderCell: ({ row }) => {
        const dueDate = moment(row?.lastDeepClean?.end).add(
          orgSettings.deepCleanFrequency ?? 12,
          'weeks',
        )
        const isOverdue = moment().isSameOrAfter(dueDate)
        const isNearlyDue = moment().isSameOrAfter(dueDate.subtract(1, 'week'))
        return (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={isOverdue ? 'red' : isNearlyDue ? 'orange' : 'inherit'}
            fontWeight={isNearlyDue ? 700 : 'inherit'}
          >
            {row?.lastDeepClean?.end
              ? moment(row?.lastDeepClean?.end).format('DD/MM/YYYY')
              : ''}
          </Box>
        )
      },
    },
    {
      field: 'lastSafetyCheck',
      headerName: 'Last Safety Check',
      align: 'center',
      headerAlign: 'center',
      minWidth: 140,
      flex: 0.3,
      valueGetter: (_, row) => row?.lastSafetyCheck?.testDate ?? null,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : '',
      renderCell: ({ row }) => {
        const dueDate = moment(row?.lastSafetyCheck?.testDate).add(
          Number(orgSettings.vehicleSafetyCheckFrequency),
          'months',
        )
        const checksDisabled =
          orgSettings.vehicleSafetyCheckFrequency === 'Disabled'
        const isOverdue = !checksDisabled && moment().isSameOrAfter(dueDate)
        const isNearlyDue =
          !checksDisabled && moment().isSameOrAfter(dueDate.subtract(1, 'week'))
        return (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={isOverdue ? 'red' : isNearlyDue ? 'orange' : 'inherit'}
            fontWeight={isNearlyDue ? 700 : 'inherit'}
          >
            {row?.lastSafetyCheck?.testDate
              ? moment(row?.lastSafetyCheck?.testDate).format('DD/MM/YYYY')
              : ''}
          </Box>
        )
      },
    },
    {
      field: 'lastServiceDate',
      headerName: 'Last Service',
      align: 'center',
      headerAlign: 'center',
      flex: 0.3,
      minWidth: 140,
      valueGetter: (_, row) => row?.lastServiceDate ?? null,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : '',
      renderCell: ({ row }) => {
        const date = moment(row.lastServiceDate)

        const servicesDisabled =
          orgSettings.vehicleServiceFrequency === 'Disabled'
        const isOverdue =
          !servicesDisabled &&
          moment()
            .subtract(orgSettings.vehicleServiceFrequency ?? 12, 'months')
            .isSameOrAfter(date)
        const isNearlyDue =
          !servicesDisabled &&
          moment()
            .subtract(orgSettings.vehicleServiceFrequency ?? 12, 'months')
            .isSameOrAfter(date.subtract(1, 'week'))

        return (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={isOverdue ? 'red' : isNearlyDue ? 'orange' : 'inherit'}
            fontWeight={isNearlyDue ? 700 : 'inherit'}
          >
            {moment(date).isValid() ? moment(date).format('DD/MM/YYYY') : ''}
          </Box>
        )
      },
    },
    {
      field: 'motExpiry',
      headerName: 'MOT',
      align: 'center',
      headerAlign: 'center',
      flex: 0.3,
      minWidth: 140,
      valueGetter: (_, row) => row?.motExpiry ?? null,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : '',
      renderCell: ({ row }) => {
        const [date, setDate] = useState(null)
        const [loading, setLoading] = useState(true)

        useEffect(() => {
          if (row.regPlate) {
            API.post('/dvla-lookup', { regPlate: row.regPlate })
              .then((res) => {
                if (res.status === 200) {
                  setDate(res.data.motExpiryDate)
                }
              })
              .catch(() => setDate(null))
              .finally(() => setLoading(false))
          }
        }, [])

        return (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={
              moment(date).isBefore(moment())
                ? 'red'
                : moment(date).isBefore(moment().add(1, 'month'))
                  ? 'orange'
                  : 'inherit'
            }
            fontWeight={
              moment(date).isBefore(moment().add(1, 'month')) ? 700 : 'inherit'
            }
          >
            {loading ? (
              <CircularProgress />
            ) : date ? (
              moment(date).format('DD/MM/YYYY')
            ) : (
              '-'
            )}
          </Box>
        )
      },
      // TODO: Add colors to dates (expired, nearly expired)
    },
    {
      field: 'tax',
      headerName: 'TAX',
      align: 'center',
      headerAlign: 'center',
      flex: 0.3,
      minWidth: 140,
      valueGetter: (_, row) => row?.dvla?.taxDueDate ?? null,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : '',
      renderCell: ({ row }) => {
        const [date, setDate] = useState(null)
        const [loading, setLoading] = useState(true)

        useEffect(() => {
          if (row.regPlate) {
            API.post('/dvla-lookup', { regPlate: row.regPlate })
              .then((res) => {
                if (res.status === 200) {
                  setDate(res.data.taxDueDate)
                }
              })
              .catch(() => setDate(null))
              .finally(() => setLoading(false))
          }
        }, [])

        return (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={
              moment(date).isBefore(moment())
                ? 'red'
                : moment(date).isBefore(moment().add(1, 'month'))
                  ? 'orange'
                  : 'inherit'
            }
            fontWeight={
              moment(date).isBefore(moment().add(1, 'month')) ? 700 : 'inherit'
            }
          >
            {loading ? (
              <CircularProgress />
            ) : date ? (
              moment(date).format('DD/MM/YYYY')
            ) : (
              '-'
            )}
          </Box>
        )
      },
      // TODO: Add colors to dates (expired, nearly expired)
    },
    {
      field: 'insuranceExpiry',
      headerName: 'Insurance',
      align: 'center',
      flex: 0.3,
      minWidth: 140,
      headerAlign: 'center',
      renderCell: ({ row }) => {
        const date = row.insuranceExpiry
        return (
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={
              moment(date).isBefore(moment())
                ? 'red'
                : moment(date).isBefore(moment().add(1, 'month'))
                  ? 'orange'
                  : 'inherit'
            }
            fontWeight={
              moment(date).isBefore(moment().add(1, 'month')) ? 700 : 'inherit'
            }
          >
            {date ? moment(date).format('DD/MM/YYYY') : '-'}
          </Box>
        )
      },
      getOptionValue: ({ row }) => moment(row.insuranceExpiry).unix(),
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : '',
      // TODO: Add colors to dates (expired, nearly expired)
    },
  ]
}

export default Columns
